import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui';
import {
  getBillingUsage,
  getStrategies,
  getOutlines,
  getContentItems,
  getHumanTasks,
  getAccountSnapshot,
  getSupervisorStatus,
} from '../api';
import type {
  BillingUsage,
  Strategy,
  ContentOutline,
  ContentItem,
  HumanTask,
  AccountSnapshotSummary,
  SupervisorStatus,
  PageKey,
  Workspace,
} from '../types';
import './Dashboard.css';

type DashboardProps = {
  workspaceId: number;
  workspace: Workspace;
  onNavigate?: (_page: PageKey) => void;
};

/* ── Pipeline state ──────────────────────────────────────────────────────── */
interface PipelineData {
  strategy: Strategy | null;
  outlines: ContentOutline[];
  items: ContentItem[];
}

interface PipelineCounts {
  outlines: number;
  drafts: number;
  approved: number;
  scheduled: number;
  published: number;
}

interface AttnItem {
  key: string;
  title: string;
  detail: string;
  cta: string;
  urgent?: boolean;
  onClick: () => void;
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function countByStatus(items: ContentItem[]): Record<string, number> {
  const map: Record<string, number> = {};
  for (const item of items) {
    map[item.status] = (map[item.status] ?? 0) + 1;
  }
  return map;
}

function formatNumber(num: number): string {
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return String(num);
}

function usagePct(used: number, limit: number): number {
  if (limit === 0) return 0;
  return Math.min(100, (used / limit) * 100);
}

function formatAllowedClipSeconds(seconds?: number[]): string {
  if (!seconds || seconds.length === 0) return '';
  return seconds.map((value) => `${value}s`).join(', ');
}

function fillClass(pct: number): string {
  if (pct >= 90) return 'usage-meter__fill--danger';
  if (pct >= 70) return 'usage-meter__fill--warning';
  return '';
}

function barWidth(count: number, max: number): string {
  if (max === 0) return '0%';
  return `${Math.min(100, (count / max) * 100)}%`;
}

function capitalize(s: string): string {
  return s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** Past-only relative time (e.g. last run). Assumes iso is not in the future. */
function relTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

/** Clock-time formatting for future timestamps (e.g. next run) — direction-safe. */
function formatClockTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
}

/* Map pipeline stage dot keys to their CSS variable colors */
const PIPELINE_BAR_COLOR: Record<string, string> = {
  outline: 'var(--color-neutral-300)',
  draft: 'var(--color-warning-main)',
  approved: 'var(--color-success-main)',
  scheduled: 'var(--color-scheduled-main)',
  published: 'var(--color-primary-600)',
};

const QUICK_ACTIONS: { label: string; page: PageKey }[] = [
  { label: 'Strategy', page: 'strategy' },
  { label: 'Content Library', page: 'content' },
  { label: 'Calendar', page: 'calendar' },
  { label: 'Schedule', page: 'schedule' },
  { label: 'Needs You', page: 'tasks' },
  { label: 'AI Assistant', page: 'assistant' },
];

/** Extract the results array from a fulfilled `Promise.allSettled` entry. */
function settledResults<T>(result: PromiseSettledResult<{ results?: T[] }>): T[] {
  return result.status === 'fulfilled' ? (result.value.results ?? []) : [];
}

/* ── Subcomponents ───────────────────────────────────────────────────────── */
function DashboardSkeleton() {
  return (
    <div className="dashboard-skeleton" aria-label="Loading dashboard…">
      <div className="dashboard-skeleton__nba" />
      <div className="dashboard-skeleton__row">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="dashboard-skeleton__tile" />
        ))}
      </div>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────────────────── */
function Dashboard({ workspaceId, workspace, onNavigate }: DashboardProps) {
  const [usage, setUsage] = useState<BillingUsage | null>(null);
  const [pipeline, setPipeline] = useState<PipelineData | null>(null);
  const [tasks, setTasks] = useState<HumanTask[]>([]);
  const [snapshot, setSnapshot] = useState<AccountSnapshotSummary | null>(null);
  const [snapshotConnected, setSnapshotConnected] = useState(false);
  const [supervisor, setSupervisor] = useState<SupervisorStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [usageData, strategiesData, outlinesData, itemsData, tasksData, snapshotData, supervisorData] =
          await Promise.allSettled([
            getBillingUsage(workspaceId),
            getStrategies(),
            getOutlines(),
            getContentItems(),
            getHumanTasks(workspaceId, { status: 'open', limit: 3 }),
            getAccountSnapshot(workspaceId),
            getSupervisorStatus(workspaceId),
          ]);

        if (cancelled) return;

        setUsage(usageData.status === 'fulfilled' ? (usageData.value as BillingUsage) : null);

        setPipeline({
          strategy:
            settledResults<Strategy>(
              strategiesData as PromiseSettledResult<{ results?: Strategy[] }>
            )[0] ?? null,
          outlines: settledResults<ContentOutline>(
            outlinesData as PromiseSettledResult<{ results?: ContentOutline[] }>
          ),
          items: settledResults<ContentItem>(
            itemsData as PromiseSettledResult<{ results?: ContentItem[] }>
          ),
        });

        if (tasksData.status === 'fulfilled') {
          setTasks(tasksData.value.tasks);
        }
        if (snapshotData.status === 'fulfilled') {
          setSnapshotConnected(snapshotData.value.connected);
          setSnapshot(snapshotData.value.snapshot);
        }
        setSupervisor(supervisorData.status === 'fulfilled' ? supervisorData.value : null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const navigate = (page: PageKey) => onNavigate?.(page);

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard__header">
          <div>
            <h1 className="dashboard__title">Dashboard</h1>
            <p className="dashboard__subtitle">Loading your account…</p>
          </div>
        </div>
        <DashboardSkeleton />
      </div>
    );
  }

  /* ── Derived data ── */
  const hasStrategy = !!pipeline?.strategy;
  const counts = pipeline ? countByStatus(pipeline.items) : {};
  const pipelineCounts: PipelineCounts = {
    outlines: pipeline?.outlines.length ?? 0,
    drafts: counts['draft'] ?? 0,
    approved: counts['approved'] ?? 0,
    scheduled: counts['scheduled'] ?? 0,
    published: counts['published'] ?? 0,
  };
  const totalItems = Object.values(pipelineCounts).reduce((a, b) => a + b, 0);

  const connected =
    snapshotConnected || Boolean(workspace.has_instagrapi_session || workspace.has_instagram_token);
  const engagement = snapshot?.engagement;
  const viewsOrLikes =
    engagement?.avg_views != null
      ? { label: 'Avg views', value: formatNumber(engagement.avg_views) }
      : engagement?.avg_likes != null
        ? { label: 'Avg likes', value: formatNumber(engagement.avg_likes) }
        : null;

  const subtitle = !hasStrategy
    ? "Let's get your account set up."
    : snapshot?.account_stage && snapshot.account_stage !== 'unknown'
      ? capitalize(snapshot.account_stage)
      : !connected
        ? 'Connect Instagram to start tracking your account.'
        : "Here's how your account is doing.";

  const attentionItems: AttnItem[] = [];
  if (hasStrategy && !connected) {
    attentionItems.push({
      key: 'connect',
      title: "Instagram isn't connected",
      detail: 'Connect your account in Settings to publish and track performance.',
      cta: 'Connect',
      onClick: () => navigate('settings'),
    });
  }
  for (const t of tasks) {
    attentionItems.push({
      key: t.id,
      title: t.title,
      detail: t.instructions,
      cta: 'View',
      urgent: t.priority === 'high',
      onClick: () => navigate('tasks'),
    });
  }

  const tokenPct =
    usage && usage.limits.monthly_tokens
      ? usagePct(usage.usage.tokens_used, usage.limits.monthly_tokens)
      : 0;
  const imagePct =
    usage && usage.limits.monthly_images
      ? usagePct(usage.usage.images_generated, usage.limits.monthly_images)
      : 0;
  const videoPct =
    usage && usage.limits.monthly_video_seconds
      ? usagePct(usage.usage.video_seconds_generated, usage.limits.monthly_video_seconds)
      : 0;
  const postsPct =
    usage && usage.limits.monthly_posts
      ? usagePct(usage.usage.posts_generated, usage.limits.monthly_posts)
      : 0;
  const postsUsageLabel =
    usage?.plan_code === 'starter'
      ? '🧠 Ideas/briefs this month'
      : '📝 Publishable posts this month';
  const allowedVideoClipSeconds = formatAllowedClipSeconds(
    usage?.video_generation?.allowed_seconds
  );

  const usageAndActions = (
    <div className="dashboard__right">
      {usage && (
        <div className="usage-card">
          <div className="usage-card__header">
            <span className="usage-card__title">Usage</span>
            <span className="usage-card__plan">{usage.plan_name}</span>
          </div>
          <div className="usage-card__body">
            {usage.limits.monthly_posts !== null && (
              <div className="usage-meter usage-meter--posts">
                <div className="usage-meter__row">
                  <span className="usage-meter__label">{postsUsageLabel}</span>
                  <span
                    className={`usage-meter__value${postsPct >= 90 ? ' usage-meter__value--danger' : postsPct >= 70 ? ' usage-meter__value--warn' : ''}`}
                  >
                    {usage.usage.posts_generated}
                    {usage.limits.monthly_posts !== null
                      ? ` / ${usage.limits.monthly_posts}`
                      : ' / ∞'}
                  </span>
                </div>
                {usage.limits.monthly_posts !== null && usage.limits.monthly_posts > 0 && (
                  <div className="usage-meter__track">
                    <div
                      className={`usage-meter__fill ${fillClass(postsPct)}`}
                      style={{ width: `${postsPct}%` }}
                    />
                  </div>
                )}
                {postsPct >= 80 && (
                  <div className="usage-meter__upgrade-hint">
                    Running low —{' '}
                    <button
                      className="usage-meter__upgrade-link"
                      onClick={() => navigate('settings')}
                    >
                      upgrade your plan
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="usage-meter">
              <div className="usage-meter__row">
                <span className="usage-meter__label">AI Tokens</span>
                <span className="usage-meter__value">
                  {formatNumber(usage.usage.tokens_used)}
                  {usage.limits.monthly_tokens && usage.limits.monthly_tokens > 0
                    ? ` / ${formatNumber(usage.limits.monthly_tokens)}`
                    : ' / ∞'}
                </span>
              </div>
              {usage.limits.monthly_tokens && usage.limits.monthly_tokens > 0 && (
                <div className="usage-meter__track">
                  <div
                    className={`usage-meter__fill ${fillClass(tokenPct)}`}
                    style={{ width: `${tokenPct}%` }}
                  />
                </div>
              )}
            </div>

            {usage.features.images && (
              <div className="usage-meter">
                <div className="usage-meter__row">
                  <span className="usage-meter__label">Images</span>
                  <span className="usage-meter__value">
                    {usage.usage.images_generated}
                    {usage.limits.monthly_images && usage.limits.monthly_images > 0
                      ? ` / ${usage.limits.monthly_images}`
                      : ' / ∞'}
                  </span>
                </div>
                {usage.limits.monthly_images && usage.limits.monthly_images > 0 && (
                  <div className="usage-meter__track">
                    <div
                      className={`usage-meter__fill ${fillClass(imagePct)}`}
                      style={{ width: `${imagePct}%` }}
                    />
                  </div>
                )}
              </div>
            )}

            {usage.features.video && (
              <div className="usage-meter">
                <div className="usage-meter__row">
                  <span className="usage-meter__label">Video</span>
                  <span className="usage-meter__value">
                    {usage.usage.video_seconds_generated}s
                    {usage.limits.monthly_video_seconds && usage.limits.monthly_video_seconds > 0
                      ? ` / ${usage.limits.monthly_video_seconds}s`
                      : ' / ∞'}
                  </span>
                </div>
                {usage.limits.monthly_video_seconds && usage.limits.monthly_video_seconds > 0 && (
                  <div className="usage-meter__track">
                    <div
                      className={`usage-meter__fill ${fillClass(videoPct)}`}
                      style={{ width: `${videoPct}%` }}
                    />
                  </div>
                )}
                {allowedVideoClipSeconds && (
                  <div className="usage-meter__detail">Clips: {allowedVideoClipSeconds}</div>
                )}
              </div>
            )}
          </div>
          <div className="usage-card__footer">
            <span>
              Resets{' '}
              {new Date(usage.period.end).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
              })}
            </span>
            <button className="usage-card__manage-link" onClick={() => navigate('settings')}>
              Manage plan →
            </button>
          </div>
        </div>
      )}

      <div className="quick-actions-card">
        <div className="quick-actions-card__header">
          <span className="quick-actions-card__title">Jump to</span>
        </div>
        <div className="quick-actions-card__list">
          {QUICK_ACTIONS.map(({ label, page }) => (
            <button key={page} className="quick-action-row" onClick={() => navigate(page)}>
              <span className="quick-action-row__label">{label}</span>
              <span className="quick-action-row__arrow">›</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="dashboard">
      <div className="dashboard__header">
        <div>
          <h1 className="dashboard__title">Dashboard</h1>
          <p className="dashboard__subtitle">{subtitle}</p>
        </div>
      </div>

      {!hasStrategy ? (
        <>
          <div className="dash-hero">
            <span className="mono-label dash-hero__eyebrow">Getting started</span>
            <h2 className="dash-hero__title">Every account starts with a conversation.</h2>
            <p className="dash-hero__body">
              Tell the Oracle your niche, audience, and vibe. It writes your strategy, drafts your
              first posts in your voice, and asks before anything goes live.
            </p>
            <div className="dash-hero__actions">
              <button className="dash-gradient-btn" onClick={() => navigate('assistant')}>
                Ask the Oracle
              </button>
              <Button variant="ghost" onClick={() => navigate('strategy')}>
                Write it yourself
              </Button>
            </div>
          </div>

          <div className="dashboard__lower">
            <div className="howitworks-card">
              <div className="howitworks-card__header">
                <span className="mono-label">How Conistra works</span>
              </div>
              <ol className="howitworks-list">
                <li>
                  <span className="howitworks-list__dot" />
                  Chat with the Oracle about your brand — it writes your strategy and content
                  pillars.
                </li>
                <li>
                  <span className="howitworks-list__dot" />
                  It drafts outlines and full posts in your voice, on-brand every time.
                </li>
                <li>
                  <span className="howitworks-list__dot" />
                  You approve and schedule — or flip on autopilot and let it publish itself.
                </li>
              </ol>
            </div>
            {usageAndActions}
          </div>
        </>
      ) : (
        <>
          {attentionItems.length > 0 && (
            <div className="attn-card">
              <div className="attn-card__header">
                <span className="mono-label">Needs you</span>
                <button className="attn-card__viewall" onClick={() => navigate('tasks')}>
                  View all →
                </button>
              </div>
              {attentionItems.map((item) => (
                <button key={item.key} className="attn-row" onClick={item.onClick}>
                  <span
                    className={`attn-row__dot${item.urgent ? ' attn-row__dot--urgent' : ''}`}
                    aria-hidden="true"
                  />
                  <span className="attn-row__body">
                    <span className="attn-row__title">{item.title}</span>
                    {item.detail && <span className="attn-row__detail">{item.detail}</span>}
                  </span>
                  <span className="attn-row__cta">{item.cta} ›</span>
                </button>
              ))}
            </div>
          )}

          <span className="mono-label pulse-row__label">Account pulse</span>
          <div className="pulse-row">
            <div className="pulse-tile">
              <span className="pulse-tile__label">Followers</span>
              <span
                className={`pulse-tile__value${connected && snapshot ? '' : ' pulse-tile__value--muted'}`}
              >
                {connected && snapshot ? formatNumber(snapshot.followers) : '—'}
              </span>
            </div>
            <div className="pulse-tile">
              <span className="pulse-tile__label">Engagement rate</span>
              <span
                className={`pulse-tile__value${engagement?.engagement_rate_pct != null ? '' : ' pulse-tile__value--muted'}`}
              >
                {engagement?.engagement_rate_pct != null
                  ? `${engagement.engagement_rate_pct}%`
                  : '—'}
              </span>
            </div>
            <div className="pulse-tile">
              <span className="pulse-tile__label">{viewsOrLikes?.label ?? 'Avg likes'}</span>
              <span className={`pulse-tile__value${viewsOrLikes ? '' : ' pulse-tile__value--muted'}`}>
                {viewsOrLikes?.value ?? '—'}
              </span>
            </div>
            <div className="pulse-tile">
              <span className="pulse-tile__label">Published</span>
              <span
                className={`pulse-tile__value${pipelineCounts.published ? '' : ' pulse-tile__value--muted'}`}
              >
                {pipelineCounts.published}
              </span>
            </div>
          </div>

          <div className="dashboard__lower">
            <div className="dashboard__left">
              <div className="pipeline-card">
                <div className="pipeline-card__header">
                  <span className="pipeline-card__title">Content Pipeline</span>
                </div>

                <div className="pipeline-stage-list">
                  {(
                    [
                      {
                        key: 'outlines',
                        label: 'Outlines',
                        dot: 'outline',
                        count: pipelineCounts.outlines,
                      },
                      {
                        key: 'drafts',
                        label: 'Drafts',
                        dot: 'draft',
                        count: pipelineCounts.drafts,
                      },
                      {
                        key: 'approved',
                        label: 'Approved',
                        dot: 'approved',
                        count: pipelineCounts.approved,
                      },
                      {
                        key: 'scheduled',
                        label: 'Scheduled',
                        dot: 'scheduled',
                        count: pipelineCounts.scheduled,
                      },
                      {
                        key: 'published',
                        label: 'Published',
                        dot: 'published',
                        count: pipelineCounts.published,
                      },
                    ] as const
                  ).map(({ key, label, dot, count }) => (
                    <div key={key} className="pipeline-stage">
                      <span className={`pipeline-stage__dot pipeline-stage__dot--${dot}`} />
                      <span className="pipeline-stage__name">{label}</span>
                      <div className="pipeline-stage__bar-wrap">
                        <div
                          className="pipeline-stage__bar"
                          style={{
                            width: barWidth(
                              count,
                              Math.max(totalItems, pipelineCounts.outlines, 1)
                            ),
                            background: PIPELINE_BAR_COLOR[dot],
                          }}
                        />
                      </div>
                      <span
                        className={`pipeline-stage__count ${count === 0 ? 'pipeline-stage__count--zero' : ''}`}
                      >
                        {count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="autopilot-card">
                <div className="autopilot-card__header">
                  <span className="mono-label">Autopilot</span>
                  <button className="autopilot-card__manage" onClick={() => navigate('supervisor')}>
                    Manage →
                  </button>
                </div>
                <div className="autopilot-card__body">
                  <span
                    className={`autopilot-card__dot${supervisor?.supervisor_enabled ? ' autopilot-card__dot--on' : ''}`}
                    aria-hidden="true"
                  />
                  <div>
                    <div className="autopilot-card__status">
                      {supervisor?.supervisor_enabled ? 'Running' : 'Paused'}
                    </div>
                    <div className="autopilot-card__detail">
                      {supervisor?.supervisor_enabled
                        ? `Last run ${relTime(supervisor.supervisor_last_run_at)} · next ${formatClockTime(supervisor.next_run_at)}`
                        : 'Turn it on to let Conistra publish for you.'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {usageAndActions}
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;
